import { useState } from "owa-react";
import classnames from "owa-classnames";
import { observer } from "owa-mobx-react";
import type { AriaProperties } from "owa-accessibility";
import { AriaRoles, generateDomPropertiesForAria } from "owa-accessibility";
import { isMonarchMultipleAccountsEnabled } from "owa-account-source-list/lib/flights";
import { getDensityMode, getDensityModeString, getDensityModeCssClass } from "owa-fabric-theme";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import isWin32StylingEnabled from "owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled";
import { useKeydownHandlers } from "owa-hotkeys";
import loc, { isCurrentCultureRightToLeft } from "owa-localize";
import React from "react";
import { getUITreeNodeDepth, getTreeNodesFarPadding } from "../utils/treeNodeUIUtils";
import { selectedFolderNodeScreenReaderOnlyText, ellipsesButtonTitle, } from "./TreeNode.locstring.json";
import { logUsage } from "owa-analytics";
import { full, medium, compact, mediumAndFull_Win32Style, compact_Win32Style, iconsNext, iconsNextFull, rootIcon, icon, folderWithChevron as styles_folderWithChevron, leftNavAllFoldersPadding, favoriteNodeNoRightPadding, folderIcons, regularNodeChevronIcon, chevronIconCompact, chevronIconMedium, chevronIconFull, chevronIcon_Win32Style, chevronPadding, chevronIconButton, chevronIconButtonFull, chevronIconButtonMedium, chevronIconButtonCompact, chevronIconButton_Win32Style, rootNode, regularNode, regularNodeActive, roundedCorners, densityNodeHeight, densityRootNodeHeight, showAsHoverOnDroppedOver as styles_showAsHoverOnDroppedOver, showBorderOnDroppedOver, isBeingDragged as styles_isBeingDragged, customActionNode, overridenFont, selected, withContextMenuOpen, disabledCursor, isDisabled as styles_isDisabled, displayName as styles_displayName, densityFontSize, allFolderIconsRootText, hiddenFolderGlyph_Win32Style, rightCharm as styles_rightCharm, hoverPadding, rightCharmHover as styles_rightCharmHover, rootNodeFont, rootNodeFont_Win32Style, chevronAnimation, chevronAnimationCollapseFull, chevronAnimationCollapse, ellipsesIcon, rtlStyles, ltrStyles, rootHover, ellipsesOnHover as styles_ellipsesOnHover, ellipsesHovered, focusStyling, focusStyling_Win32Style, ellipsesPadding, ellipsesOnHoverTreatmentPresent, rightCharmHoverTreatmentPresent, fullDenseless, mediumDenseless, rootNodeV2, regularNodeV2, selectedV2, ellipsesIconV2, nodeActiveV2, withContextMenuOpenV2, verticalNode, verticalNodeRtl, } from "./TreeNode.scss";
import { useMouseWithPerfWrapper } from "owa-react-hooks/lib/useMouseWithPerfWrapper";
import { useRestoreFocusTarget, Button, makeStyles } from "@fluentui/react-components";
import { type FluentIcon, ChevronDownRegular, ChevronDownFilled, ChevronRightRegular, ChevronRightFilled, MoreHorizontalRegular, MoreHorizontalFilled, FolderRegular, FolderFilled, bundleIcon, iconRegularClassName, iconFilledClassName, } from "@fluentui/react-icons";
import type { MailboxInfo } from "owa-client-types";

export type ChevronProps = {
    isExpanded: boolean;
    onClick: (evt: React.MouseEvent<unknown> | KeyboardEvent) => void;
    iconButtonClassName?: string;
    iconClassName?: string;
    isAnimated?: boolean;
};

export interface TreeNodeProps {
    displayName: string;
    isRootNode: boolean;
    
    // Optional parameters
    chevronProps?: ChevronProps; // Chevron related props, if not passed, we will not show chevron
    customIconComponent?: FluentIcon; // Render an icon for special tree nodes
    customIconClassName?: string;
    onRenderCustomIcon?: (customIconClassName: string) => React.JSX.Element;
    depth?: number; // The indentation int the tree node structure, where 0 is the root
    isBeingDragged?: boolean;
    isCustomActionNode?: boolean; // A node that contains a custom action (e.g. "New folder" node that creates new folder)
    isDroppedOver?: boolean;
    isSelected?: boolean;
    isWithContextMenuOpen?: boolean;
    onClick?: React.MouseEventHandler<EventTarget>;
    onKeyDown?: React.KeyboardEventHandler<EventTarget>;
    onContextMenu?: React.MouseEventHandler<EventTarget>;
    onMouseEnter?: React.MouseEventHandler<EventTarget>;
    onTouchStart?: React.TouchEventHandler<EventTarget>;
    onTouchEnd?: React.TouchEventHandler<EventTarget>;
    renderCustomTreeNodeDisplay?: (textClassName: string) => React.JSX.Element; // Method to render the main content (if not specified the tree node will render the default UI)
    renderRightCharm?: () => React.JSX.Element | null; // Method to render the right charm (if any)
    renderRightCharmHover?: () => React.JSX.Element | null; // Method to render the right charm on hover state (if any)
    isFavorited?: boolean;
    toggleFavorite?: () => void;
    showAsHoverOnDroppedOver?: boolean; // True if we want to show the hover style css when the tree node is dropped over
    isCursorDisabled?: boolean;
    setSize?: number;
    positionInSet?: number;
    isinSharedFolderTree?: boolean;
    ariaLabel?: string;
    isDisabled?: boolean;
    idUsedForTooltip?: string;
    elementRef?: React.RefObject<HTMLDivElement | null>;
    customTreeNodeTooltip?: string;
    
    ellipsesOnHover?: boolean;
    source?: string;
    shouldShowFocusBorder?: boolean;
    ariaIsDisabled?: boolean;
    id?: string;
    customIconColor?: string;
    mailboxInfo?: MailboxInfo;
    parentRef?: React.RefObject<HTMLDivElement | null>;
    useNewSelectionUI?: boolean;
    isVerticalNode?: boolean;
    hideIcon?: boolean;
    style?: React.CSSProperties;
}

const defaultExport: React.FunctionComponent<TreeNodeProps> = observer(function TreeNode(props: TreeNodeProps) {
    const localRef = React.useRef<HTMLDivElement>(null);
    const divRef = props.elementRef ?? localRef;
    
    const isRPDenselessEnabled = isGlobalFeatureEnabled("mon-type-ramp");
    const densityModeCssClass = getDensityModeCssClass(classnames(full, isRPDenselessEnabled && fullDenseless), classnames(medium, isRPDenselessEnabled && mediumDenseless), compact);
    const isWin32Style = isWin32StylingEnabled();
    
    const chevronProps = props.chevronProps;
    const ellipsesOnHover = props.ellipsesOnHover;
    const [mountEllipses, setMountEllipses] = useState(false);
    const [isEllipsesHovered, setIsEllipsesHovered] = useState(false);
    const ellipsesRef = React.useRef<HTMLSpanElement>(null);
    const restoreFocusTargetAttribute = useRestoreFocusTarget();
    const useNewSelectionUI = props.useNewSelectionUI;
    
    const { customIconComponent, customIconClassName, idUsedForTooltip, onRenderCustomIcon, parentRef, isVerticalNode, hideIcon, } = props;
    
    const isRTL = isCurrentCultureRightToLeft();
    const getContainerStyle = () => {
        if (isVerticalNode) {
            return {
                paddingLeft: 0,
                paddingRight: 0,
                width: "100%",
                ...props.style,
            };
        }
        
        // The first level of tree nodes must be inline with root in the UI
        const firstLevelPadding = 0;
        const hasDepthOrIsFavorited = !!depth || props.isFavorited;
        const uiTreeNodeDepth = hasDepthOrIsFavorited
            ? getUITreeNodeDepth(depth ?? 0, getDensityModeString())
            : firstLevelPadding;
        const treePaddingValue = uiTreeNodeDepth + (hasDepthOrIsFavorited ? firstLevelPadding : 0);
        const treePadding = treePaddingValue + "px";
        const shouldUseWin32RowOffset = isWin32Style && (hasDepthOrIsFavorited || isRootNode);
        const win32OffsetBase = hasDepthOrIsFavorited ? treePaddingValue : 0;
        const win32RowOffset = shouldUseWin32RowOffset ? `${win32OffsetBase + 8}px` : treePadding;
        const farPadding = ellipsesOnHover ? undefined : getTreeNodesFarPadding();
        const width = "auto";
        const paddingRight = isRTL ? (shouldUseWin32RowOffset ? "0px" : treePadding) : farPadding;
        const paddingLeft = isRTL ? farPadding : shouldUseWin32RowOffset ? "0px" : treePadding;
        const marginLeft = !isRTL && shouldUseWin32RowOffset ? win32RowOffset : undefined;
        const marginRight = isRTL && shouldUseWin32RowOffset ? win32RowOffset : undefined;
        
        // Just add paddin so the hover of the tree node is the same width
        // independent on its depth
        return {
            paddingLeft,
            paddingRight,
            marginLeft,
            marginRight,
            width,
            ...props.style,
        };
    };
    
    useKeydownHandlers(divRef, React.useCallback(() => [
        {
            command: "right",
            handler: (evt: KeyboardEvent) => {
                if (chevronProps?.onClick && !chevronProps.isExpanded) {
                    chevronProps.onClick(evt);
                }
            },
        },
        {
            command: "left",
            handler: (evt: KeyboardEvent) => {
                if (chevronProps?.onClick && chevronProps.isExpanded) {
                    chevronProps.onClick(evt);
                }
                else if (parentRef?.current) {
                    parentRef.current.focus();
                }
            },
        },
    ], [chevronProps?.onClick, chevronProps?.isExpanded]));
    
    const openContextMenu = (evt: React.MouseEvent, src: string) => {
        evt.stopPropagation();
        
        logUsage("FP_OpenContextMenu", props.mailboxInfo, {
            source: props.source,
            eventSource: src,
        });
        onContextMenu?.(evt);
    };
    
    const rightClickContextMenu = React.useCallback((evt: React.MouseEvent<HTMLDivElement>) => {
        openContextMenu(evt, "Right Click");
    }, [openContextMenu]);
    
    const { isCustomActionNode, isDroppedOver, isSelected, showAsHoverOnDroppedOver, isBeingDragged, isWithContextMenuOpen, isCursorDisabled, isRootNode, depth, onClick, onKeyDown, onContextMenu, onMouseEnter, onTouchStart, onTouchEnd, displayName, renderCustomTreeNodeDisplay, renderRightCharm, renderRightCharmHover, isinSharedFolderTree, isDisabled, customTreeNodeTooltip, shouldShowFocusBorder, customIconColor, } = props;
    
    const treeNodeClasses = classnames(isRootNode && !useNewSelectionUI ? rootNode : regularNode, useNewSelectionUI ? (isRootNode ? rootNodeV2 : regularNodeV2) : undefined, useNewSelectionUI && nodeActiveV2, densityModeCssClass, isWin32Style &&
        // We want to use the same density mode for the folder icon as the chevron icon,
        // so we use the mediumAndFull_Win32Style for both full and medium density modes,
        // and compact_Win32Style for compact density mode.
        getDensityModeCssClass(mediumAndFull_Win32Style, mediumAndFull_Win32Style, compact_Win32Style), roundedCorners, 
    // Vertical node styles
    !isVerticalNode && (isRootNode ? densityRootNodeHeight : densityNodeHeight), isVerticalNode && verticalNode, isVerticalNode && isRTL && verticalNodeRtl, !isVerticalNode && (ellipsesOnHover && isRTL ? rtlStyles : ltrStyles), shouldShowFocusBorder && focusStyling, shouldShowFocusBorder && isWin32Style && focusStyling_Win32Style, {
        [styles_showAsHoverOnDroppedOver]: isDroppedOver && showAsHoverOnDroppedOver,
        [showBorderOnDroppedOver]: isDroppedOver && !showAsHoverOnDroppedOver,
        [styles_isBeingDragged]: isBeingDragged,
        [customActionNode]: overridenFont && isCustomActionNode,
        [selected]: isSelected,
        [disabledCursor]: isCursorDisabled,
        [styles_isDisabled]: !!isDisabled,
        [ellipsesHovered]: isEllipsesHovered,
    }, useNewSelectionUI
        ? {
            [withContextMenuOpenV2]: isWithContextMenuOpen && !isSelected,
            [selectedV2]: isSelected,
        }
        : {
            [withContextMenuOpen]: isWithContextMenuOpen && !isSelected,
            [regularNodeActive]: !isRootNode,
        });
    
    const ariaProps: AriaProperties = {
        role: AriaRoles.treeitem,
        expanded: chevronProps ? chevronProps.isExpanded : undefined,
        selected: !isRootNode ? isSelected : undefined,
        // temporary fix for OW:15414 because it is currently possible for depth to be null causing aria-level to be NaN
        // aria-level is an integer equal to or greater than 1 where 1 is the root
        level: depth == null ? (isRootNode ? 1 : 2) : depth + 1,
        setSize: props.setSize,
        positionInSet: props.positionInSet,
        label: props.ariaLabel,
        disabled: props.ariaIsDisabled,
    };
    
    const handleMouseEnter = React.useCallback((event: React.MouseEvent<EventTarget, MouseEvent>) => {
        if (ellipsesOnHover) {
            setMountEllipses(true);
        }
        if (onMouseEnter) {
            onMouseEnter(event);
        }
    }, 
    /* eslint-disable-next-line owa-custom-rules/no-unused-react-set-state -- (https://aka.ms/OWALintWiki)
     * DO NOT COPY-PASTE!, add a justification or fix the issue
     *	> React setState functions from useState should only be used by direct invocation, setState(), not passed or used as a value or used in hook's array dependencies. */
    [setMountEllipses, onMouseEnter]);
    const mouseEnterCb = useMouseWithPerfWrapper(handleMouseEnter, "TreeNode2");
    
    const handleMouseLeave = React.useCallback(() => {
        if (ellipsesOnHover) {
            setMountEllipses(false);
        }
        /* eslint-disable-next-line owa-custom-rules/no-unused-react-set-state -- (https://aka.ms/OWALintWiki)
         * DO NOT COPY-PASTE!, add a justification or fix the issue
         *	> React setState functions from useState should only be used by direct invocation, setState(), not passed or used as a value or used in hook's array dependencies. */
    }, [setMountEllipses]);
    const mouseLeaveCb = useMouseWithPerfWrapper(handleMouseLeave, "TreeNode2");
    
    return (<div ref={
        // TODO (456964): Remove this React 18 compatibility cast after adopting React 19 types.
        divRef as React.Ref<HTMLDivElement>} id={props.id} style={getContainerStyle()} onClick={onClick} onKeyDown={onKeyDown} onContextMenu={rightClickContextMenu} onMouseEnter={isGlobalFeatureEnabled("fwk-treenode-always-render") ? onMouseEnter : mouseEnterCb} onMouseLeave={isGlobalFeatureEnabled("fwk-treenode-always-render") ? undefined : mouseLeaveCb} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} className={treeNodeClasses} data-is-focusable={true} title={customTreeNodeTooltip ?? displayName} tabIndex={-1} {...generateDomPropertiesForAria(ariaProps)} data-folder-name={displayName.toLowerCase()} {...restoreFocusTargetAttribute}>
            {
        // decides which icon to render on the left of the display name
        // We want to give all folders a folder Icon, except the root folders.
        // Hide the icon slot entirely, unless Win32 (FolderIcon keeps a zero-width spacer).
        hideIcon && !isWin32Style ? null : isRootNode ? (<TreeNodeIcon {...{
            chevronProps,
            customIconComponent,
            customIconClassName,
            idUsedForTooltip,
            onRenderCustomIcon,
        }}/>) : (<FolderIcon {...{
            isSelected,
            isFavorited: props.isFavorited,
            chevronProps,
            customIconComponent,
            customIconClassName,
            
            idUsedForTooltip,
            isCustomActionNode,
            onRenderCustomIcon,
            customIconColor,
            hideIcon,
        }}/>)}

            <TreeNodeDisplay {...{
        displayName,
        isRootNode,
        isSelected,
        renderCustomTreeNodeDisplay,
    }}/>

            <RightCharm {...{
        ellipsesOnHover,
        isinSharedFolderTree,
        renderRightCharm,
        renderRightCharmHover,
    }}/>
            {(mountEllipses || isGlobalFeatureEnabled("fwk-treenode-always-render")) && (<EllipsesIcon {...{
            ellipsesOnHover,
            isRootNode,
            openContextMenu,
            /* eslint-disable-next-line owa-custom-rules/no-unused-react-set-state -- (https://aka.ms/OWALintWiki)
             * DO NOT COPY-PASTE!, add a justification or fix the issue
             *	> React setState functions from useState should only be used by direct invocation, setState(), not passed or used as a value or used in hook's array dependencies.
             *	> React setState functions from useState should only be used by direct invocation, setState(), not passed or used as a value or used in hook's array dependencies. */
            setIsEllipsesHovered,
            ellipsesRef,
            useNewSelectionUI,
        }}/>)}
        </div>);
}, "TreeNode");
export default defaultExport;

const DefaultFolderIcon = bundleIcon(FolderFilled, FolderRegular);
const useStyles = makeStyles({
    icon: {
        [`& .${iconFilledClassName}`]: {
            display: "none",
        },
        [`& .${iconRegularClassName}`]: {
            display: "flex",
        },
    },
    iconSelected: {
        [`& .${iconFilledClassName}`]: {
            display: "flex",
        },
        [`& .${iconRegularClassName}`]: {
            display: "none",
        },
    },
});

export interface CustomIconProps {
    isSelected?: boolean;
    customIconComponent?: FluentIcon;
    customIconClassNames: string;
    
    idUsedForTooltip?: string;
    customIconColor?: string;
}
const CustomIcon = observer(function CustomIcon({ isSelected, customIconComponent, customIconClassNames, 

idUsedForTooltip, customIconColor, }: CustomIconProps) {
    const isDenselessEnabled = isGlobalFeatureEnabled("mon-type-ramp");
    const densityModeCssClass = getDensityModeCssClass(iconsNextFull, isDenselessEnabled ? iconsNextFull : undefined, undefined);
    const styles = useStyles();
    
    const classNameForIcon = classnames(customIconClassNames, iconsNext, densityModeCssClass);
    const CustomFluentIcon = customIconComponent;
    const customColor = React.useMemo(() => {
        return customIconColor
            ? ({ "--customColor": customIconColor } as React.CSSProperties)
            : undefined;
    }, [customIconColor]);
    return (<div className={isSelected ? styles.iconSelected : styles.icon} style={customColor}>
            {CustomFluentIcon ? (<CustomFluentIcon className={classNameForIcon} id={isGlobalFeatureEnabled("acct-multiacctcomingsoon")
                ? idUsedForTooltip
                : undefined}/>) : (<DefaultFolderIcon className={classNameForIcon}/>)}
        </div>);
}, "CustomIcon");

// Make this button hidden to narrator/jaws since all the aria
// attributes are set on the main content. If we dont do this, narrator reads the content twice.
// This button is hidden from aria/tabs but its action can be performed from the main content as well
// We cant make this an icon since there are consumers
// that uses the click handler from the button different from the chevron
const ariaPropsButton: AriaProperties = {
    role: "button",
    hidden: true,
};

export interface ChevronIconProps {
    chevronProps: ChevronProps;
    isRootNode: TreeNodeProps["isRootNode"];
}
const ChevronIcon = observer(function ChevronIcon({ chevronProps, isRootNode }: ChevronIconProps) {
    const isMultiAccountEnabled = isMonarchMultipleAccountsEnabled();
    const isWin32Style = isWin32StylingEnabled();
    
    const chevronOnClick = React.useCallback((evt: React.MouseEvent) => {
        evt.stopPropagation();
        chevronProps?.onClick(evt);
    }, [chevronProps?.onClick]);
    
    const chevronAnimationClassNames = classnames(chevronAnimation, !chevronProps?.isExpanded &&
        getDensityModeCssClass(chevronAnimationCollapseFull, chevronAnimationCollapse, chevronAnimationCollapse));
    
    const chevronIconClassNames = classnames(icon, regularNodeChevronIcon, getDensityModeCssClass(chevronIconFull, chevronIconMedium, chevronIconCompact), isWin32Style && chevronIcon_Win32Style, chevronProps?.iconClassName, chevronProps?.isAnimated ? chevronAnimationClassNames : "flipForRtl");
    
    const chevronIcon = React.useMemo(() => {
        const isExpanded = chevronProps?.isExpanded || chevronProps?.isAnimated;
        const ChevronDownIcon = isWin32Style ? ChevronDownFilled : ChevronDownRegular;
        const ChevronRightIcon = isWin32Style ? ChevronRightFilled : ChevronRightRegular;
        const ChevronIconToUse = isExpanded ? ChevronDownIcon : ChevronRightIcon;
        
        return <ChevronIconToUse className={chevronIconClassNames}/>;
    }, [chevronProps?.isAnimated, chevronProps?.isExpanded, chevronIconClassNames, isWin32Style]);
    
    const buttonClassName = !(isMultiAccountEnabled && isRootNode)
        ? classnames(isMultiAccountEnabled && chevronPadding, chevronIconButton, isWin32Style && chevronIconButton_Win32Style, getDensityModeCssClass(chevronIconButtonFull, chevronIconButtonMedium, chevronIconButtonCompact), chevronProps?.iconButtonClassName)
        : classnames(chevronIconButton, chevronPadding, isWin32Style && chevronIconButton_Win32Style);
    
    return (<Button appearance="transparent" data-is-focusable={false} tabIndex={-1} className={buttonClassName} icon={chevronIcon} onClick={chevronOnClick} size="small" {...generateDomPropertiesForAria(ariaPropsButton)}/>);
}, "ChevronIcon");

export interface TreeNodeIconProps {
    chevronProps: TreeNodeProps["chevronProps"];
    customIconComponent: TreeNodeProps["customIconComponent"];
    customIconClassName: TreeNodeProps["customIconClassName"];
    idUsedForTooltip: TreeNodeProps["idUsedForTooltip"];
    onRenderCustomIcon: TreeNodeProps["onRenderCustomIcon"];
}
const TreeNodeIcon = observer(function TreeNodeIcon({ chevronProps, customIconComponent, customIconClassName, idUsedForTooltip, onRenderCustomIcon, }: TreeNodeIconProps) {
    // If tree node has a chevron, always display it (even if there is also a custom icon).
    // Otherwise display a custom icon or nothing if no custom icon is passed in.
    if (chevronProps) {
        return <ChevronIcon {...{ chevronProps }} isRootNode={true}/>;
    }
    
    const customIconClassNames = classnames(customIconComponent && rootIcon, icon, customIconClassName);
    
    if (onRenderCustomIcon) {
        return onRenderCustomIcon(customIconClassNames);
    }
    
    if (customIconComponent) {
        return <CustomIcon {...{ customIconComponent, customIconClassNames, idUsedForTooltip }}/>;
    }
    
    return null; // Existing code should never reach here
}, "TreeNodeIcon");

export interface FolderIconProps {
    isSelected: TreeNodeProps["isSelected"];
    isFavorited: TreeNodeProps["isFavorited"];
    chevronProps: TreeNodeProps["chevronProps"];
    customIconComponent: TreeNodeProps["customIconComponent"];
    customIconClassName: TreeNodeProps["customIconClassName"];
    
    idUsedForTooltip: TreeNodeProps["idUsedForTooltip"];
    isCustomActionNode: TreeNodeProps["isCustomActionNode"];
    onRenderCustomIcon: TreeNodeProps["onRenderCustomIcon"];
    customIconColor: TreeNodeProps["customIconColor"];
    hideIcon: TreeNodeProps["hideIcon"];
}
const FolderIcon = observer(function FolderIcon({ isSelected, isFavorited, chevronProps, customIconComponent, customIconClassName, 

idUsedForTooltip, isCustomActionNode, onRenderCustomIcon, customIconColor, hideIcon, }: FolderIconProps) {
    const densityClass = getDensityModeCssClass(full, medium, compact);
    const isWin32Style = isWin32StylingEnabled();
    
    const customIconClassNames = classnames(icon, isCustomActionNode && !customIconComponent && "visibilityHidden", customIconClassName, leftNavAllFoldersPadding, isWin32Style && isFavorited && !chevronProps && favoriteNodeNoRightPadding, densityClass, isWin32Style &&
        // We want to use the same density mode for the folder icon as the chevron icon,
        // so we use the mediumAndFull_Win32Style for both full and medium density modes,
        // and compact_Win32Style for compact density mode.
        getDensityModeCssClass(mediumAndFull_Win32Style, mediumAndFull_Win32Style, compact_Win32Style), chevronProps && styles_folderWithChevron);
    
    return (<div className={folderIcons}>
            {chevronProps && <ChevronIcon {...{ chevronProps }} isRootNode={false}/>}

            {hideIcon ? (<div className={classnames(customIconClassNames, hiddenFolderGlyph_Win32Style)}/>) : onRenderCustomIcon ? (onRenderCustomIcon(customIconClassNames)) : (<CustomIcon {...{
            isSelected,
            customIconComponent,
            customIconClassNames,
            
            idUsedForTooltip,
            customIconColor,
        }}/>)}
        </div>);
}, "FolderIcon");

export interface TreeNodeDisplayProps {
    displayName: TreeNodeProps["displayName"];
    isRootNode: TreeNodeProps["isRootNode"];
    isSelected: TreeNodeProps["isSelected"];
    renderCustomTreeNodeDisplay: TreeNodeProps["renderCustomTreeNodeDisplay"];
}
const TreeNodeDisplay = observer(function TreeNodeDisplay({ displayName, isRootNode, isSelected, renderCustomTreeNodeDisplay, }: TreeNodeDisplayProps) {
    const isDenselessEnabled = isGlobalFeatureEnabled("mon-type-ramp");
    const densityModeCssClass = getDensityModeCssClass(classnames(full, isDenselessEnabled && fullDenseless), classnames(medium, isDenselessEnabled && mediumDenseless), compact);
    
    const isWin32Style = isWin32StylingEnabled();
    
    const textClassNames = classnames(styles_displayName, densityFontSize, densityModeCssClass, isWin32Style &&
        // We want to use the same density mode for the folder icon as the chevron icon,
        // so we use the mediumAndFull_Win32Style for both full and medium density modes,
        // and compact_Win32Style for compact density mode.
        getDensityModeCssClass(mediumAndFull_Win32Style, mediumAndFull_Win32Style, compact_Win32Style), {
        [selected]: isSelected,
    }, isRootNode && allFolderIconsRootText, isRootNode && rootNodeFont, isRootNode && isWin32Style && rootNodeFont_Win32Style);
    
    if (renderCustomTreeNodeDisplay) {
        return renderCustomTreeNodeDisplay(textClassNames);
    }
    
    return (<>
            <span className={textClassNames}>{displayName}</span>
            {isSelected && (<span className="screenReaderOnly">
                    {loc(selectedFolderNodeScreenReaderOnlyText)}
                </span>)}
        </>);
}, "TreeNodeDisplay");

export interface RightCharmProps {
    ellipsesOnHover: TreeNodeProps["ellipsesOnHover"];
    isinSharedFolderTree: TreeNodeProps["isinSharedFolderTree"];
    renderRightCharm: TreeNodeProps["renderRightCharm"];
    renderRightCharmHover: TreeNodeProps["renderRightCharmHover"];
}
const RightCharm = observer(function RightCharm({ ellipsesOnHover, isinSharedFolderTree, renderRightCharm, renderRightCharmHover, }: RightCharmProps) {
    const isMultiAccountEnabled = isMonarchMultipleAccountsEnabled();
    
    /**
     * Hover treatment is optional and hence the CSS classes to hide the rest treatment with onHover treatment
     * should only be applied if hover treatment is specified.
     */
    const rightCharmClassName = classnames(styles_rightCharm, isMultiAccountEnabled &&
        !isinSharedFolderTree &&
        renderRightCharmHover &&
        rightCharmHoverTreatmentPresent, isMultiAccountEnabled && hoverPadding, ellipsesOnHover && rightCharmHoverTreatmentPresent);
    
    const rightCharm = renderRightCharm?.();
    const rightCharmHover = renderRightCharmHover?.();
    
    return (<>
            {rightCharm && <span className={rightCharmClassName}>{rightCharm}</span>}
            {rightCharmHover && <span className={styles_rightCharmHover}>{rightCharmHover}</span>}
        </>);
}, "RightCharm");

export interface EllipsesIconProps {
    ellipsesOnHover: TreeNodeProps["ellipsesOnHover"];
    isRootNode: TreeNodeProps["isRootNode"];
    openContextMenu: (evt: React.MouseEvent, src: string) => void;
    /* eslint-disable-next-line owa-custom-rules/no-unused-react-set-state -- (https://aka.ms/OWALintWiki)
     * DO NOT COPY-PASTE!, add a justification or fix the issue
     *	> React setState functions from useState should only be used by direct invocation, setState(), not passed or used as a value or used in hook's array dependencies. */
    setIsEllipsesHovered: React.Dispatch<React.SetStateAction<boolean>>;
    ellipsesRef: React.RefObject<HTMLSpanElement | null>;
    useNewSelectionUI?: boolean;
}

const MoreHorizontalIcon = bundleIcon(MoreHorizontalFilled, MoreHorizontalRegular);
const EllipsesIcon = observer(function EllipsesIcon({ ellipsesOnHover, isRootNode, openContextMenu, 
/* eslint-disable-next-line owa-custom-rules/no-unused-react-set-state -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, add a justification or fix the issue
 *	> React setState functions from useState should only be used by direct invocation, setState(), not passed or used as a value or used in hook's array dependencies.
 *	> React setState functions from useState should only be used by direct invocation, setState(), not passed or used as a value or used in hook's array dependencies. */
setIsEllipsesHovered, ellipsesRef, useNewSelectionUI, }: EllipsesIconProps) {
    const density = getDensityMode();
    const densityModeCssClass = getDensityModeCssClass(full, medium, compact);
    const ellipseClassName = classnames(styles_ellipsesOnHover, ellipsesOnHover && ellipsesOnHoverTreatmentPresent);
    
    enum ellipsisFontSize {
        Compact = 16,
        Full = 20,
        Simple = 18
    }
    const ellipsesIconComponent = React.useMemo(() => {
        const fontSize = ellipsisFontSize[density] + "px";
        return (<MoreHorizontalIcon fontSize={fontSize} className={useNewSelectionUI ? undefined : ellipsesPadding}/>);
    }, [density, useNewSelectionUI]);
    
    const ellipsesContextMenu = React.useCallback((evt: React.MouseEvent) => {
        openContextMenu(evt, "Ellipses");
    }, [openContextMenu]);
    
    const handleMouseEnterEllipses = React.useCallback(() => {
        setIsEllipsesHovered(true);
        /* eslint-disable-next-line owa-custom-rules/no-unused-react-set-state -- (https://aka.ms/OWALintWiki)
         * DO NOT COPY-PASTE!, add a justification or fix the issue
         *	> React setState functions from useState should only be used by direct invocation, setState(), not passed or used as a value or used in hook's array dependencies. */
    }, [setIsEllipsesHovered]);
    
    const handleMouseLeaveEllipses = React.useCallback(() => {
        setIsEllipsesHovered(false);
        /* eslint-disable-next-line owa-custom-rules/no-unused-react-set-state -- (https://aka.ms/OWALintWiki)
         * DO NOT COPY-PASTE!, add a justification or fix the issue
         *	> React setState functions from useState should only be used by direct invocation, setState(), not passed or used as a value or used in hook's array dependencies. */
    }, [setIsEllipsesHovered]);
    
    const mouseEnterEllipsesCb = useMouseWithPerfWrapper(handleMouseEnterEllipses, "TreeNode1");
    const mouseLeaveEllipsesCb = useMouseWithPerfWrapper(handleMouseLeaveEllipses, "TreeNode1");
    
    const classes = classnames(ellipsesIcon, useNewSelectionUI ? null : isRootNode && rootHover, densityModeCssClass, useNewSelectionUI && ellipsesIconV2);
    
    return (<span className={ellipseClassName} ref={
        // TODO (456964): Remove this React 18 compatibility cast after adopting React 19 types.
        ellipsesRef as React.Ref<HTMLSpanElement>}>
            <Button title={loc(ellipsesButtonTitle)} appearance="transparent" data-is-focusable={false} tabIndex={-1} className={classes} icon={ellipsesIconComponent} onClick={ellipsesContextMenu} onMouseEnter={mouseEnterEllipsesCb} onMouseLeave={mouseLeaveEllipsesCb} {...generateDomPropertiesForAria(ariaPropsButton)}/>
        </span>);
}, "EllipsesIcon");
